import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  FileText,
  FileCode2,
  FileType2,
  Download,
  Loader2,
  AlertCircle,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import { ScrollArea } from "@foundation/components/ui/scroll-area";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { AdminLibraryItem } from "../types/adminLibrary";
import {
  adminLibraryService,
  type DocumentPreviewUrls,
} from "../services/adminLibraryService";

type DocumentPreviewModalProps = {
  item: AdminLibraryItem | null;
  onClose: () => void;
};

type LoadState = "idle" | "loading" | "success" | "error";

const FILE_TYPE_META: Record<
  string,
  { label: string; color: string; bg: string; Icon: React.ElementType }
> = {
  pdf: {
    label: "PDF",
    color: "text-red-600",
    bg: "bg-red-50 border-red-200",
    Icon: FileType2,
  },
  docx: {
    label: "DOCX",
    color: "text-blue-600",
    bg: "bg-blue-50 border-blue-200",
    Icon: FileText,
  },
  txt: {
    label: "TXT",
    color: "text-slate-600",
    bg: "bg-slate-50 border-slate-200",
    Icon: FileCode2,
  },
  md: {
    label: "MD",
    color: "text-violet-600",
    bg: "bg-violet-50 border-violet-200",
    Icon: FileCode2,
  },
};

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function PdfViewer({ url }: { url: string }) {
  const [scale, setScale] = useState(100);

  return (
    <div className="flex flex-col h-full">
      {/* PDF toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50/80 backdrop-blur-sm shrink-0">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setScale((s) => Math.max(50, s - 10))}
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-slate-500 w-12 text-center tabular-nums">
            {scale}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setScale((s) => Math.min(200, s + 10))}
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setScale(100)}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ExternalLink className="h-3 w-3" />
          Open in new tab
        </a>
      </div>
      <div className="flex-1 overflow-auto bg-slate-100">
        <div
          className="transition-transform duration-200 origin-top"
          style={{ transform: `scale(${scale / 100})`, transformOrigin: "top center" }}
        >
          <iframe
            src={`${url}#toolbar=0&navpanes=0`}
            className="w-full border-0"
            style={{ height: "calc(100vh - 280px)", minHeight: 500 }}
            title="PDF Preview"
          />
        </div>
      </div>
    </div>
  );
}

// Shared ReactMarkdown component overrides — explicit Tailwind classes bypass Tailwind's
// preflight reset which zeroes out all heading/table styles.
const MD_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  h1: ({ children }) => (
    <h1 style={{ fontSize: "1.65rem", fontWeight: 700, borderBottom: "2px solid #e2e8f0", paddingBottom: "0.5rem", marginTop: "1.5rem", marginBottom: "1.25rem", color: "#0f172a", lineHeight: 1.3 }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: "1.3rem", fontWeight: 700, marginTop: "1.75rem", marginBottom: "0.6rem", color: "#1e293b", lineHeight: 1.35 }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: "1.1rem", fontWeight: 600, marginTop: "1.25rem", marginBottom: "0.5rem", color: "#334155", lineHeight: 1.4 }}>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 style={{ fontSize: "1rem", fontWeight: 600, marginTop: "1rem", marginBottom: "0.4rem", color: "#475569" }}>{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 style={{ fontSize: "0.95rem", fontWeight: 600, marginTop: "0.75rem", marginBottom: "0.35rem", color: "#475569" }}>{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 style={{ fontSize: "0.9rem", fontWeight: 600, marginTop: "0.75rem", marginBottom: "0.3rem", color: "#64748b" }}>{children}</h6>
  ),
  p: ({ children }) => (
    <p style={{ lineHeight: 1.75, color: "#334155", marginBottom: "0.85rem" }}>{children}</p>
  ),
  strong: ({ children }) => (
    <strong style={{ fontWeight: 700, color: "#0f172a" }}>{children}</strong>
  ),
  em: ({ children }) => (
    <em style={{ fontStyle: "italic", color: "#334155" }}>{children}</em>
  ),
  ul: ({ children }) => (
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem", marginBottom: "0.85rem" }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ listStyleType: "decimal", paddingLeft: "1.5rem", marginBottom: "0.85rem" }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ lineHeight: 1.7, color: "#334155", marginBottom: "0.25rem" }}>{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: "4px solid #94a3b8", padding: "0.75rem 1rem", color: "#64748b", fontStyle: "italic", margin: "1rem 0", background: "#f8fafc", borderRadius: "0 4px 4px 0" }}>{children}</blockquote>
  ),
  code: ({ inline, children }: { inline?: boolean; children?: React.ReactNode }) =>
    inline ? (
      <code style={{ background: "#f1f5f9", color: "#475569", padding: "0.15rem 0.4rem", borderRadius: "4px", fontSize: "0.85em", fontFamily: "monospace" }}>{children}</code>
    ) : (
      <code>{children}</code>
    ),
  pre: ({ children }) => (
    <pre style={{ background: "#1e293b", color: "#e2e8f0", padding: "1rem 1.25rem", borderRadius: "8px", overflowX: "auto", marginBottom: "1rem", fontSize: "0.875em" }}>{children}</pre>
  ),
  a: ({ href, children }) => (
    <a href={href} style={{ color: "#2563eb", textDecoration: "none" }} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  hr: () => (
    <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "1.5rem 0" }} />
  ),
  // Table components — requires remark-gfm
  table: ({ children }) => (
    <div style={{ overflowX: "auto", marginBottom: "1.25rem" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ background: "#f1f5f9" }}>{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody>{children}</tbody>
  ),
  tr: ({ children }) => (
    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>{children}</tr>
  ),
  th: ({ children }) => (
    <th style={{ padding: "0.65rem 0.9rem", fontWeight: 600, textAlign: "left", color: "#1e293b", borderBottom: "2px solid #cbd5e1", borderRight: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{children}</th>
  ),
  td: ({ children }) => (
    <td style={{ padding: "0.55rem 0.9rem", color: "#334155", borderBottom: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0", verticalAlign: "top" }}>{children}</td>
  ),
};

function MarkdownViewer({ content }: { content: string }) {
  return (
    <ScrollArea className="h-full">
      <div style={{ padding: "2rem", maxWidth: "800px", margin: "0 auto", fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: "0.95rem" }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {content}
        </ReactMarkdown>
      </div>
    </ScrollArea>
  );
}

function TextViewer({ content }: { content: string }) {
  return (
    <ScrollArea className="h-full">
      <div className="p-8">
        <pre
          className="
            font-mono text-sm leading-relaxed text-slate-700
            whitespace-pre-wrap break-words
            bg-slate-50 rounded-lg p-6 border border-slate-200
          "
        >
          {content}
        </pre>
      </div>
    </ScrollArea>
  );
}

function EmptyState({ fileType }: { fileType: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-8">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
        <FileText className="h-8 w-8 text-slate-400" />
      </div>
      <div>
        <p className="font-medium text-slate-700">Preview not available</p>
        <p className="text-sm text-slate-500 mt-1">
          {fileType
            ? `The ${fileType.toUpperCase()} file could not be rendered inline.`
            : "This file type cannot be previewed."}
        </p>
        <p className="text-xs text-slate-400 mt-2">
          Use the download button to view the file.
        </p>
      </div>
    </div>
  );
}

const MIME_TO_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "docx",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/x-markdown": "md",
};
const KNOWN_EXTENSIONS = new Set(["pdf", "docx", "doc", "txt", "md"]);

function normalizeFileType(raw: string, filename?: string): string {
  const lower = raw.toLowerCase().trim();
  if (KNOWN_EXTENSIONS.has(lower)) return lower;
  if (MIME_TO_EXT[lower]) return MIME_TO_EXT[lower];
  // Fall back to filename extension
  if (filename) {
    const ext = filename.split(".").pop()?.toLowerCase() || "";
    if (KNOWN_EXTENSIONS.has(ext)) return ext;
  }
  return lower;
}

export function DocumentPreviewModal({ item, onClose }: DocumentPreviewModalProps) {
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [preview, setPreview] = useState<DocumentPreviewUrls | null>(null);
  const [error, setError] = useState<string>("");

  const fetchPreview = useCallback(async (id: string) => {
    setLoadState("loading");
    setError("");
    try {
      const data = await adminLibraryService.getDocumentPreviewUrls(id);
      setPreview(data);
      setLoadState("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load preview");
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    if (!item) {
      setPreview(null);
      setLoadState("idle");
      return;
    }
    fetchPreview(item.id);
  }, [item, fetchPreview]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!item) return null;

  const fileType = normalizeFileType(preview?.fileType || item.file_type || "", preview?.filename || item.title);
  const typeMeta = FILE_TYPE_META[fileType] || FILE_TYPE_META["txt"];
  const { Icon } = typeMeta;

  const renderDocumentContent = () => {
    if (loadState === "loading") {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          <p className="text-sm text-slate-500">Loading document…</p>
        </div>
      );
    }

    if (loadState === "error") {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-8">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <div>
            <p className="font-medium text-slate-700">Could not load preview</p>
            <p className="text-sm text-slate-500 mt-1">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchPreview(item.id)}>
            Try again
          </Button>
        </div>
      );
    }

    if (!preview) return null;

    // PDF — use iframe with signed URL
    if (fileType === "pdf" && preview.fileUrl) {
      return <PdfViewer url={preview.fileUrl} />;
    }

    // DOCX — prefer markdown conversion, fall back to mammoth.js client-side rendering
    if (fileType === "docx") {
      if (preview.markdownUrl || preview.textContent) {
        return (
          <DocxViewerAsync
            markdownSignedUrl={preview.markdownUrl}
            fallbackText={preview.textContent}
          />
        );
      }
      if (preview.fileUrl) {
        return <DocxBinaryViewer fileUrl={preview.fileUrl} />;
      }
      return <EmptyState fileType={fileType} />;
    }

    // TXT — inline text content
    if (fileType === "txt") {
      if (preview.textContent) return <TextViewer content={preview.textContent} />;
      if (preview.fileUrl) return <DocxViewerAsync markdownSignedUrl={preview.fileUrl} isTxt />;
      return <EmptyState fileType={fileType} />;
    }

    // MD — render markdown
    if (fileType === "md") {
      if (preview.textContent) return <MarkdownViewer content={preview.textContent} />;
      if (preview.markdownUrl) return <DocxViewerAsync markdownSignedUrl={preview.markdownUrl} />;
      if (preview.fileUrl) return <DocxViewerAsync markdownSignedUrl={preview.fileUrl} />;
      return <EmptyState fileType={fileType} />;
    }

    return <EmptyState fileType={fileType} />;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        style={{
          animation: "docPreviewFadeIn 0.18s ease-out",
        }}
        onClick={onClose}
      />

      {/* Modal panel */}
      <div
        className="relative z-10 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: "min(92vw, 1100px)",
          height: "min(92vh, 800px)",
          animation: "docPreviewSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-start gap-4 px-6 py-4 border-b border-slate-100 bg-white shrink-0">
          {/* File type icon */}
          <div
            className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 mt-0.5 ${typeMeta.bg}`}
          >
            <Icon className={`h-5 w-5 ${typeMeta.color}`} />
          </div>

          {/* Title + meta */}
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-slate-900 leading-tight truncate pr-4">
              {item.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <Badge
                variant="outline"
                className={`text-xs font-medium ${typeMeta.bg} ${typeMeta.color} border-0 h-5 px-2`}
              >
                {typeMeta.label}
              </Badge>
              {item.file_size && (
                <span className="text-xs text-slate-400">
                  {formatFileSize(item.file_size)}
                </span>
              )}
              {item.description && (
                <span className="text-xs text-slate-500 truncate max-w-xs">
                  {item.description}
                </span>
              )}
            </div>
          </div>

          {/* Close — top-right corner */}
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 h-9 w-9 p-0 -mt-1 -mr-1.5"
            onClick={onClose}
            aria-label="Close preview"
            title="Close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* ── Content area ── */}
        <div className="flex-1 overflow-hidden bg-slate-50/40 dark:bg-slate-900/40 doc-preview-content">
          {renderDocumentContent()}
        </div>

        {/* ── Footer tag strip ── */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex items-center gap-2 px-6 py-2.5 border-t border-slate-100 bg-white/80 shrink-0 overflow-x-auto">
            <span className="text-xs text-slate-400 shrink-0">Tags</span>
            {item.tags.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                className="text-xs h-5 px-2 bg-slate-50 text-slate-600 border-slate-200 shrink-0 capitalize"
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes docPreviewFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes docPreviewSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>

      {/* Dark-mode content colors — override the hardcoded light-theme inline
          styles so markdown/DOCX content stays readable on the dark panel. */}
      <style>{`
        .dark .doc-preview-content { color: #e5e7eb; }
        .dark .doc-preview-content h1,
        .dark .doc-preview-content h2,
        .dark .doc-preview-content h3 { color: #f9fafb !important; border-bottom-color: hsl(var(--border)) !important; }
        .dark .doc-preview-content h4,
        .dark .doc-preview-content h5,
        .dark .doc-preview-content h6 { color: #e5e7eb !important; }
        .dark .doc-preview-content p,
        .dark .doc-preview-content li,
        .dark .doc-preview-content td { color: #e5e7eb !important; }
        .dark .doc-preview-content strong,
        .dark .doc-preview-content b { color: #ffffff !important; }
        .dark .doc-preview-content em,
        .dark .doc-preview-content blockquote { color: #cbd5e1 !important; }
        .dark .doc-preview-content blockquote { border-left-color: hsl(var(--border)) !important; background: hsl(var(--accent) / 0.3) !important; }
        .dark .doc-preview-content code { background: hsl(var(--primary) / 0.15) !important; color: #f1f5f9 !important; }
        .dark .doc-preview-content pre { background: #0f172a !important; color: #e2e8f0 !important; }
        .dark .doc-preview-content a { color: #60a5fa !important; }
        .dark .doc-preview-content hr { border-top-color: hsl(var(--border)) !important; }
        .dark .doc-preview-content table,
        .dark .doc-preview-content th,
        .dark .doc-preview-content td { border-color: hsl(var(--border)) !important; }
        .dark .doc-preview-content thead { background: hsl(var(--primary) / 0.12) !important; }
        .dark .doc-preview-content th { color: #f9fafb !important; }

        .dark .docx-render { color: #e5e7eb !important; }
        .dark .docx-render h1 { color: #f9fafb !important; border-bottom-color: hsl(var(--border)) !important; }
        .dark .docx-render h2 { color: #f9fafb !important; }
        .dark .docx-render h3 { color: #e5e7eb !important; }
        .dark .docx-render h4,
        .dark .docx-render h5,
        .dark .docx-render h6 { color: #e5e7eb !important; }
        .dark .docx-render p { color: #e5e7eb !important; }
        .dark .docx-render strong,
        .dark .docx-render b { color: #ffffff !important; }
        .dark .docx-render li { color: #e5e7eb !important; }
        .dark .docx-render a { color: #60a5fa !important; }
        .dark .docx-render blockquote { color: #cbd5e1 !important; border-left-color: hsl(var(--border)) !important; background: hsl(var(--accent) / 0.3) !important; }
        .dark .docx-render hr { border-top-color: hsl(var(--border)) !important; }
        .dark .docx-render table,
        .dark .docx-render th,
        .dark .docx-render td { border-color: hsl(var(--border)) !important; }
        .dark .docx-render thead tr { background: hsl(var(--primary) / 0.12) !important; }
        .dark .docx-render th { color: #f9fafb !important; }
        .dark .docx-render td { color: #e5e7eb !important; }
        .dark .docx-render tbody tr:nth-child(even) { background: hsl(var(--primary) / 0.06) !important; }
        .dark .docx-render tbody tr:hover { background: hsl(var(--primary) / 0.12) !important; }
      `}</style>
    </div>
  );
}

// Async loader for docx markdown / txt fetched from a signed URL
type DocxViewerAsyncProps = {
  markdownSignedUrl?: string;
  fallbackText?: string;
  isTxt?: boolean;
};

function DocxViewerAsync({ markdownSignedUrl, fallbackText, isTxt }: DocxViewerAsyncProps) {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string>("");

  useEffect(() => {
    if (fallbackText) {
      setContent(fallbackText);
      setLoading(false);
      return;
    }
    if (!markdownSignedUrl) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    fetch(markdownSignedUrl, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("Failed to fetch document content:", err);
        setFetchError(err instanceof Error ? err.message : "Failed to load content");
        setLoading(false);
      });

    return () => controller.abort();
  }, [markdownSignedUrl, fallbackText]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Rendering document…</span>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="font-medium text-slate-700">Failed to load document</p>
        <p className="text-sm text-slate-500">{fetchError}</p>
      </div>
    );
  }

  if (!content) return <EmptyState fileType="" />;

  return isTxt ? <TextViewer content={content} /> : <MarkdownViewer content={content} />;
}

// Renders DOCX files by fetching the binary and converting via mammoth.js
function DocxBinaryViewer({ fileUrl }: { fileUrl: string }) {
  const [html, setHtml] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(fileUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();

        const mammoth = await import("mammoth");
        const result = await mammoth.convertToHtml({
          arrayBuffer,
          styleMap: [
            "p[style-name='Title'] => h1:fresh",
            "p[style-name='Subtitle'] => p.subtitle:fresh",
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "p[style-name='Heading 4'] => h4:fresh",
            "p[style-name='Heading 5'] => h5:fresh",
            "p[style-name='heading 1'] => h1:fresh",
            "p[style-name='heading 2'] => h2:fresh",
            "p[style-name='heading 3'] => h3:fresh",
          ],
        });
        setHtml(result.value);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("Failed to render DOCX:", err);
        setFetchError(err instanceof Error ? err.message : "Failed to render document");
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [fileUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Rendering document…</span>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="font-medium text-slate-700">Failed to render document</p>
        <p className="text-sm text-slate-500">{fetchError}</p>
      </div>
    );
  }

  if (!html) return <EmptyState fileType="docx" />;

  // Inject a <style> block with !important rules so Tailwind's preflight reset cannot
  // override the heading/table styles that mammoth generates.
  const docxStyles = `
    .docx-render { font-family: 'DM Sans', system-ui, sans-serif; font-size: 0.95rem !important; line-height: 1.75 !important; color: #334155 !important; }
    .docx-render h1 { font-size: 1.65rem !important; font-weight: 700 !important; border-bottom: 2px solid #e2e8f0; padding-bottom: 0.5rem; margin-top: 1.5rem !important; margin-bottom: 1.25rem !important; color: #0f172a !important; line-height: 1.3 !important; }
    .docx-render h2 { font-size: 1.3rem !important; font-weight: 700 !important; margin-top: 1.75rem !important; margin-bottom: 0.6rem !important; color: #1e293b !important; line-height: 1.35 !important; }
    .docx-render h3 { font-size: 1.1rem !important; font-weight: 600 !important; margin-top: 1.25rem !important; margin-bottom: 0.5rem !important; color: #334155 !important; line-height: 1.4 !important; }
    .docx-render h4 { font-size: 1rem !important; font-weight: 600 !important; margin-top: 1rem !important; margin-bottom: 0.4rem !important; color: #475569 !important; }
    .docx-render h5, .docx-render h6 { font-size: 0.95rem !important; font-weight: 600 !important; margin-top: 0.75rem !important; margin-bottom: 0.35rem !important; color: #475569 !important; }
    .docx-render p { margin-bottom: 0.85rem !important; color: #334155 !important; line-height: 1.75 !important; }
    .docx-render strong, .docx-render b { font-weight: 700 !important; color: #0f172a !important; }
    .docx-render em, .docx-render i { font-style: italic !important; }
    .docx-render u { text-decoration: underline !important; }
    .docx-render ul { list-style-type: disc !important; padding-left: 1.5rem !important; margin-bottom: 0.85rem !important; }
    .docx-render ol { list-style-type: decimal !important; padding-left: 1.5rem !important; margin-bottom: 0.85rem !important; }
    .docx-render li { line-height: 1.7 !important; margin-bottom: 0.25rem !important; color: #334155 !important; }
    .docx-render a { color: #2563eb !important; }
    .docx-render a:hover { text-decoration: underline !important; }
    .docx-render blockquote { border-left: 4px solid #94a3b8 !important; padding: 0.75rem 1rem !important; color: #64748b !important; font-style: italic !important; margin: 1rem 0 !important; background: #f8fafc !important; border-radius: 0 4px 4px 0 !important; }
    .docx-render hr { border: none !important; border-top: 1px solid #e2e8f0 !important; margin: 1.5rem 0 !important; }
    .docx-render table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 1.25rem !important; font-size: 0.9rem !important; border: 1px solid #e2e8f0 !important; }
    .docx-render thead tr { background: #f1f5f9 !important; }
    .docx-render th { padding: 0.65rem 0.9rem !important; font-weight: 600 !important; text-align: left !important; color: #1e293b !important; border-bottom: 2px solid #cbd5e1 !important; border-right: 1px solid #e2e8f0 !important; }
    .docx-render td { padding: 0.55rem 0.9rem !important; color: #334155 !important; border-bottom: 1px solid #e2e8f0 !important; border-right: 1px solid #e2e8f0 !important; vertical-align: top !important; }
    .docx-render tbody tr:nth-child(even) { background: #fafafa !important; }
    .docx-render tbody tr:hover { background: #f0f9ff !important; }
    .docx-render img { max-width: 100% !important; height: auto !important; border-radius: 4px; margin: 0.5rem 0 !important; }
  `;

  return (
    <ScrollArea className="h-full">
      <style>{docxStyles}</style>
      <div style={{ padding: "2rem", maxWidth: "800px", margin: "0 auto" }}>
        <div className="docx-render" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </ScrollArea>
  );
}
